import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FC, MouseEvent, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { AppRole } from "@monorepo/core";

import useAuth from "../hooks/auth.hook";
import { displayFontFamily } from "../theme/mtgTheme";
import ThemeModeToggle from "./theme-mode-toggle.component";

const formatRoleLabel = (role: AppRole) =>
  role === AppRole.Admin ? "admin" : "member";

const AppHeader: FC = () => {
  const { email, isAdmin, logout, setViewRole, viewRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountMenuAnchor, setAccountMenuAnchor] =
    useState<HTMLElement | null>(null);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const navigationItems = [
    { label: "Decks", to: "/decks" },
    { label: "Card Imports", to: "/" },
  ];

  const isAccountMenuOpen = Boolean(accountMenuAnchor);
  const isNavigationItemActive = (path: string) =>
    path === "/"
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleAccountMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={(theme) => ({
        borderBottom: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.6),
        backgroundColor: alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.82 : 0.9,
        ),
        backdropFilter: "blur(18px)",
      })}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            py: 1.25,
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component={RouterLink}
                to="/"
                variant="h6"
                sx={{
                  color: "text.primary",
                  textDecoration: "none",
                  fontFamily: displayFontFamily,
                }}
              >
                MTG Deck Builder
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {navigationItems.map((item) => {
                const isActive = isNavigationItemActive(item.to);

                return (
                  <Button
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    variant="text"
                    sx={{
                      minWidth: 0,
                      px: 0,
                      color: isActive ? "text.primary" : "text.secondary",
                      fontWeight: isActive ? 700 : 500,
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            onClick={handleAccountMenuOpen}
          >
            Account
          </Button>

          <Menu
            anchorEl={accountMenuAnchor}
            open={isAccountMenuOpen}
            onClose={handleAccountMenuClose}
            PaperProps={{
              sx: {
                minWidth: 280,
                border: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  wordBreak: "break-word",
                }}
              >
                {email ?? "Unknown user"}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <ThemeModeToggle embedded />
            </Box>
            <Divider />
            {isAdmin ? (
              <MenuItem
                onClick={() => {
                  setViewRole(isAdminRoute ? AppRole.Member : AppRole.Admin);
                  navigate(isAdminRoute ? "/" : "/admin");
                  handleAccountMenuClose();
                }}
              >
                <ListItemText
                  primary={isAdminRoute ? "Member Experience" : "Admin Control"}
                  secondary={
                    isAdminRoute
                      ? "Return to the member-facing app experience."
                      : `Currently viewing as ${formatRoleLabel(viewRole)}`
                  }
                />
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                handleAccountMenuClose();
                logout();
              }}
            >
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default AppHeader;
